import { useMemo, useRef } from "react";
import { Grid, Box, Button, Stack } from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { DataGrid } from "@mui/x-data-grid";
import { CustomMUITableContainer } from "./customMUIComponents";
import { createSearchColumns } from "./createSearchColumns";
import { ExportCsvButton } from "./ExportCsvButton.component";

/**
 * Generic server-paginated table.
 *
 * The parent owns data fetching (see useServerSidePagination) and passes the
 * current page's `rows` plus the full `rowCount`; this grid renders that page
 * and reports page changes back through `onPaginationModelChange`.
 *
 * Client-side sorting/filtering are disabled: only one page of data is present
 * at a time. When `enableSearch` is set, each column header embeds a
 * server-driven search control (see createSearchColumns / ColumnHeaderSearch)
 * and the parent drives filtering through `searchModel`. Server-side sorting is
 * layered on later via `sortModel` / `onSortChange`.
 *
 * Columns come from either `columnConfig` (required when `enableSearch`) or a
 * pre-built `columns` array (plain, non-searchable usage).
 *
 * @param {Array}  [columnConfig]  search-aware column config (see createSearchColumns).
 * @param {Array}  [columns]       pre-built MUI column defs (when not searching).
 * @param {Array|undefined} rows   current page's rows (undefined → loading).
 * @param {number} rowCount        total rows across all pages.
 * @param {object} paginationModel { page, pageSize }.
 * @param {Function} [getRowId]    row → unique id (required when rows lack `id`).
 * @param {Function} [onRowClick]  row-click handler (e.g. open a details dialog).
 * @param {boolean} [enableSearch] render per-column server-side search headers.
 * @param {object} [searchModel]   active column search, keyed by field.
 * @param {Function} [onSearchChange] (field, value) => void.
 * @param {Function} [onClearAllSearch] clears every active column search.
 * @param {number} [activeFilterCount] number of active column filters.
 * @param {number} [filterResetKey] bump to remount the header inputs.
 * @param {Array}  [sortModel]     [{ field, sort }] — pass to show/drive sort.
 * @param {Function} [onSortChange] (field) => void — pass to enable header sort.
 */
export function ServerDataGridTable({
  title,
  description,
  columnConfig,
  columns,
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  getRowId,
  onRowClick,
  button,
  columnGrouping,
  columnGroupingModel,
  enableSearch = false,
  searchModel = {},
  onSearchChange,
  onClearAllSearch,
  activeFilterCount = 0,
  filterResetKey = 0,
  sortModel = [],
  onSortChange,
  onExport,
  exportCap,
}) {
  // Always-fresh reference to the current search model, so a memoized header can
  // seed its control from the latest value without rebuilding the columns on
  // every keystroke.
  const searchModelRef = useRef(searchModel);
  searchModelRef.current = searchModel;

  const gridColumns = useMemo(() => {
    const built =
      enableSearch && columnConfig
        ? createSearchColumns({
            columnConfig,
            searchModelRef,
            onSearchChange,
            filterResetKey,
            // Only wire sort once a handler is provided (later step).
            sortModel: onSortChange ? sortModel : undefined,
            onSortChange,
          })
        : columns ?? [];
    // With server-side paging the grid only holds one page, so local
    // sorting/filtering would mislead — force them off for every column.
    return built.map((col) => ({ ...col, sortable: false, filterable: false }));
    // searchModelRef is a ref (stable); intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableSearch,
    columnConfig,
    columns,
    onSearchChange,
    filterResetKey,
    sortModel,
    onSortChange,
  ]);

  return (
    <CustomMUITableContainer>
      <Box>
        <Grid
          container
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Grid item xs={6}>
            <h2 style={{ paddingLeft: "20px" }}>{title}</h2>
            <p style={{ paddingLeft: "20px" }}>{description}</p>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {button && button}
          </Grid>
        </Grid>

        {/* Toolbar: clear-all-filters (search mode) + Download CSV export. */}
        {((enableSearch && onClearAllSearch) || onExport) && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ marginLeft: "20px", paddingBottom: "10px" }}
          >
            {enableSearch && onClearAllSearch && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterAltOffIcon />}
                onClick={onClearAllSearch}
                disabled={!activeFilterCount}
                sx={{ borderRadius: "10px", whiteSpace: "nowrap" }}
              >
                {activeFilterCount
                  ? `Clear Filters (${activeFilterCount})`
                  : "Clear Filters"}
              </Button>
            )}
            {onExport && (
              <ExportCsvButton
                onExport={onExport}
                rowCount={rowCount}
                loading={!rows}
                cap={exportCap}
              />
            )}
          </Stack>
        )}

        <Box
          sx={{
            "& .detailsColumn": {
              display: "flex !important",
              alignItems: "flex-start !important",
            },
          }}
        >
          <DataGrid
            rows={rows ?? []}
            loading={!rows}
            autoHeight={true}
            columns={gridColumns}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            rowCount={rowCount ?? 0}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            getRowId={getRowId}
            getRowHeight={() => "auto"}
            // Taller header row to fit the embedded search controls.
            columnHeaderHeight={enableSearch ? 96 : undefined}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            onRowClick={onRowClick ? (e) => onRowClick(e) : null}
            getCellClassName={(params) =>
              params.field === "Details" ? "detailsColumn" : ""
            }
            experimentalFeatures={{ columnGrouping: Boolean(columnGrouping) }}
            columnGroupingModel={columnGroupingModel}
            sx={{
              border: 0,
              ...(enableSearch
                ? {
                    "& .MuiDataGrid-columnHeaderTitleContainer": {
                      alignItems: "flex-start",
                    },
                    // Let the custom header content fill the column so the
                    // embedded controls (esp. the select) can stretch full width.
                    "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                      flex: 1,
                      width: "100%",
                      overflow: "visible",
                    },
                  }
                : {}),
            }}
          />
        </Box>
      </Box>
    </CustomMUITableContainer>
  );
}
